<div
	#baseContainer
	class="relative"
	[style.padding-top]="formTopPadding"
	id="rating-form"
	data-ts1-rating-shell="v4"
>
	@if (!inPreviewSummary) {
		<!-- Sticky header -->
		<div
			class="ts1-rating-form-v3__fixed-header fixed z-20 bg-white px-6 pt-6 pb-4"
			[class.ts1-rating-form-v3__fixed-header--with-comment]="showEnableCommentToggle"
			[style.width.px]="baseContainerWidthPx > 0 ? baseContainerWidthPx : null"
		>
			<div class="relative flex w-full items-center justify-between">
				<h2 class="!text-[16px] !font-bold !leading-6" id="rating-title-id">eTS-1: Flight Crew Training</h2>

				<!-- Stepper quick nav -->
				<div class="custom-stepper">
					<button
						(click)="stepper.selectedIndex = 0"
						[class.completed]="stepper.selectedIndex > 0"
						[class.active]="stepper.selectedIndex === 0"
					>
						Training Information
					</button>
					<span class="aa-mat-icon">chevron_right</span>
					<button
						(click)="stepper.selectedIndex = 1"
						[class.completed]="stepper.selectedIndex > 1"
						[class.active]="stepper.selectedIndex === 1"
						[disabled]="stepper.selectedIndex < 1"
					>
						Candidate Rating
					</button>
					<span class="aa-mat-icon">chevron_right</span>
					<button
						type="button"
						(click)="navigateToReviewStep()"
						[class.completed]="stepper.selectedIndex > 2"
						[class.active]="stepper.selectedIndex === 2"
						[disabled]="stepper.selectedIndex < 2"
					>
						Review &amp; Final Rating
					</button>
					<span class="aa-mat-icon">chevron_right</span>
					<button
						(click)="stepper.selectedIndex = 3"
						[class.completed]="stepper.selectedIndex > 3"
						[class.active]="stepper.selectedIndex === 3"
						[disabled]="stepper.selectedIndex < 3"
					>
						Sign-off
					</button>
				</div>
			</div>

			@if (showEnableCommentToggle) {
				<div class="ts1-rating-form-v3__header-toolbar">
					<div class="ts1-rating-form-v3__enable-comment">
						<button
							type="button"
							class="ts1-rating-form-v3__enable-comment-switch"
							[class.ts1-rating-form-v3__enable-comment-switch--on]="obCommentsEnabled"
							role="switch"
							[attr.aria-checked]="obCommentsEnabled"
							aria-label="Enable Comment"
							(click)="onObCommentsEnabledChange(!obCommentsEnabled)"
						>
							<span class="ts1-rating-form-v3__enable-comment-switch-track" aria-hidden="true"></span>
							<span class="ts1-rating-form-v3__enable-comment-switch-thumb" aria-hidden="true"></span>
						</button>
						<span class="ts1-rating-form-v3__enable-comment-label">Enable Comment</span>
					</div>
				</div>
			}
		</div>
	}

	<div [hidden]="inPreviewSummary">
		<mat-stepper [linear]="true" #stepper (selectionChange)="onStepperSelectionChange($event.selectedIndex)">
			<!-- STEP 1: Training Info -->
			<mat-step [stepControl]="ts1TrainingInfoForm">
				@if (ts1TrainingInfoForm) {
					<form [formGroup]="ts1TrainingInfoForm" class="space-y-6 px-6 lg:px-0">
						<div class="grid grid-cols-2 gap-4 gap-y-0">
							<!-- Form Type -->
							<app-cbta-autocomplete
								label="Form Type"
								[data]="formType.value"
								placeholder="Form Type"
								[options]="sharedService.dropDownFormTypeOptions"
								(focusOut)="saveFormType($event)"
								[field]="formType"
								[isDisabled]="disableForm"
								[clearable]="true"
								[selectOnly]="true"
							></app-cbta-autocomplete>

							<!-- Form Name -->
							<div class="form-element">
								<mat-label for="form-name">Form Name</mat-label>
								<mat-form-field appearance="outline" class="form-field-responsive mt-1 w-full">
									<input type="text" formControlName="formName" matInput placeholder="Training Name" />
									@if (formName.errors) {
										<mat-error>{{ sharedService.getErrorMessage(formName) }}</mat-error>
									}
								</mat-form-field>
							</div>

							<!-- Test Date (Start) -->
							<app-cbta-datepicker
								[inputDesign]="'formType'"
								label="Test Date"
								placeHolder="DD MMM YYYY"
								[format]="DATE_FORMAT.INPUT_DISPLAY_DATE_FORMAT"
								[defaultValue]="trainingStartDate"
								[isDisabled]="false"
								(selectedDateChange)="onStartDateChange($event)"
							></app-cbta-datepicker>

							<!-- Syllabus End Date (kept for model binding; hidden visually if you want) -->
							<app-cbta-datepicker
								class="hidden"
								[inputDesign]="'formType'"
								label="Syllabus End Date"
								placeHolder="DD MMM YYYY"
								[format]="DATE_FORMAT.INPUT_DISPLAY_DATE_FORMAT"
								[defaultValue]="trainingEndDate"
								[minDate]="trainingEndDate"
								[isDisabled]="disableForm"
							></app-cbta-datepicker>
						</div>

						<!-- Candidates -->
						<ng-container formArrayName="candidates">
							@if (candidates.controls.length > 0) {
								<mat-accordion [multi]="true">
									@for (candidateCtrl of candidates.controls; track $index; let i = $index) {
										<mat-expansion-panel
											class="mat-elevation-z1"
											[expanded]="isCandidatePanelExpanded(i)"
											(expandedChange)="setCandidatePanelExpanded(i, $event)"
										>
											<mat-expansion-panel-header class="bg-blue-20 text-black">
												<mat-panel-title>
													<span
														[attr.id]="'ts1-candidate-header-' + i"
														class="ts1-candidate-header text-lg font-medium leading-6 text-black"
													>
														{{
															i === 0 ? "Primary Candidate" : i === 1 ? "Secondary Candidate" : "Candidate " + (i + 1)
														}}
													</span>
												</mat-panel-title>
											</mat-expansion-panel-header>

											<app-candidate-details-group-flight-v2
												[showAircraftSimulatorToggle]="true"
												[hideFlightSimulatorSection]="true"
												[useSessionRemarkModal]="true"
												[useTs1V3FlightDetails]="true"
												[sessionLayoutMode]="isEts1AocLayout ? 'ets1-aoc' : 'legacy'"
												[ets1SessionColumns]="ets1SessionColumnTree"
												[ets1TemplateSessionCodes]="formSessionDetails"
												[ets1TemplateSessionRemarks]="formSessionRemarksByCode"
												[candidateForm]="candidates.at(i)"
												[candidateIndex]="i"
												[sessions]="sessionConfigs"
											></app-candidate-details-group-flight-v2>
										</mat-expansion-panel>
									}
								</mat-accordion>

								<div class="relative my-6">
									<!-- line behind -->
									<div class="absolute inset-0 flex items-center" aria-hidden="true">
										<div class="w-full border-t border-gray-300"></div>
									</div>

									<!-- button on top -->
									<div class="relative flex w-full justify-center gap-2">
										@if (candidates.controls.length < MAX_CANDIDATES_TS1) {
											<button
												type="button"
												(click)="addNewCandidate()"
												class="z-[1] flex h-[35px] min-w-[226px] items-center justify-center gap-1 rounded bg-green px-4 text-sm text-white"
												[disabled]="!canAddCandidate()"
												[matTooltip]="!canAddCandidate() ? 'You can only add up to 4 candidates' : ''"
											>
												<span class="aa-mat-icon">add_box</span>
												<span>Add candidate</span>
											</button>
										}

										@if (candidates.controls.length >= 2) {
											<button
												type="button"
												(click)="removeCandidate(candidates.controls.length - 1)"
												class="z-[1] flex h-[35px] min-w-[226px] items-center justify-center gap-1 rounded bg-[#FF5151] px-4 text-sm text-white"
												[disabled]="candidates.controls.length < 2"
											>
												<span class="aa-mat-icon">indeterminate_check_box</span>
												<span>Remove candidate</span>
											</button>
										}
									</div>
								</div>

								<app-candidate-details-group-flight-v2
									[hideCandidateFields]="true"
									[showAircraftSimulatorToggle]="true"
									[useSessionRemarkModal]="true"
									[useTs1V3FlightDetails]="true"
									[sessionLayoutMode]="isEts1AocLayout ? 'ets1-aoc' : 'legacy'"
									[ets1SessionColumns]="ets1SessionColumnTree"
									[ets1TemplateSessionCodes]="formSessionDetails"
									[ets1TemplateSessionRemarks]="formSessionRemarksByCode"
									[candidateForm]="candidates.at(0)"
									[candidatesArray]="candidates"
									[candidateIndex]="0"
									[sessions]="sessionConfigs"
									class="mt-4 block"
								></app-candidate-details-group-flight-v2>
							}
						</ng-container>

						@if (showEts1SessionRemarkRequiredSummary) {
							<div class="mb-4 text-sm text-red">
								Please enter required details for the selected sessions marked with Edit *.
							</div>
						}

						<!-- Actions -->
						<div class="ts1-rating-form-v3__step-actions flex flex-row justify-between gap-4">
							<button type="button" data-variant="tertiary" class="aa-btn" (click)="goback()" [disabled]="!isOnline">
								Cancel
							</button>
							<button
								type="button"
								[disabled]="ts1TrainingInfoForm.invalid"
								data-variant="tertiary"
								class="aa-btn"
								(click)="saveAsTemp(1)"
							>
								Save As Draft
							</button>
							<div class="flex-1"></div>
							<button
								type="button"
								[disabled]="isStep1StartRatingDisabled"
								data-variant="primary"
								class="aa-btn"
								(click)="onStepperNext(stepper)"
							>
								Start Rating
							</button>
						</div>
					</form>
				}
			</mat-step>

			<!-- STEP 2: Rating -->
			<mat-step [stepControl]="ts1RatingForm" class="relative">
				@if (ts1RatingForm && stepper.selectedIndex === 1) {
					<form [formGroup]="ts1RatingForm" (ngSubmit)="onRatingFormSubmit()">
						<div class="ts1-rating-form-v3__page-nav" aria-label="Page navigation">
							<button
								type="button"
								class="ts1-rating-form-v3__page-nav-btn"
								(click)="collapseAllObSections()"
								matTooltip="Collapse All"
								matTooltipPosition="left"
								aria-label="Collapse All"
							>
								<mat-icon>unfold_less</mat-icon>
							</button>
							<button
								type="button"
								class="ts1-rating-form-v3__page-nav-btn"
								(click)="expandAllObSections()"
								matTooltip="Expand All"
								matTooltipPosition="left"
								aria-label="Expand All"
							>
								<mat-icon>unfold_more</mat-icon>
							</button>
							<span class="ts1-rating-form-v3__page-nav-divider" aria-hidden="true"></span>
							<button
								type="button"
								class="ts1-rating-form-v3__page-nav-btn"
								(click)="scrollToTop()"
								matTooltip="Go to Top"
								matTooltipPosition="left"
								aria-label="Go to Top"
							>
								<mat-icon>keyboard_arrow_up</mat-icon>
							</button>
							<button
								type="button"
								class="ts1-rating-form-v3__page-nav-btn"
								(click)="scrollToBottom()"
								matTooltip="Go to Bottom"
								matTooltipPosition="left"
								aria-label="Go to Bottom"
							>
								<mat-icon>keyboard_arrow_down</mat-icon>
							</button>
						</div>

						<div>
							<app-ts1-rating-step2-v2
								[ratingForm]="ratingForm"
								[loaded]="ratingFormLoaded"
								[candidateNames]="candidateList"
								[candidateCrewRoles]="candidateCrewRoles"
								[maneuverOutcomes]="maneuverOutcomes"
								[assessmentConfig]="assessmentConfig"
								[obCommentsEnabled]="obCommentsEnabled"
								[showConditionalObHighlight]="true"
								(swapRoles)="swapCrewRoles()"
								(maneuverOutcomesChange)="onManeuverOutcomesChange($event)"
								(activeManeuverChange)="queueRepeatDisabledSync()"
							></app-ts1-rating-step2-v2>
						</div>

						<div class="ts1-rating-form-v3__step-actions ts1-rating-form-v3__step2-actions flex flex-row justify-between gap-4">
							<div class="flex flex-row items-center gap-4">
								<button type="button" data-variant="tertiary" class="aa-btn" (click)="onStepBack(stepper)">Back</button>
								<button
									type="button"
									data-variant="tertiary"
									class="aa-btn"
									[disabled]="repeatDisabled"
									(click)="onRepeatManeuver()"
								>
									Repeat
								</button>
							</div>

							<div class="flex-1"></div>
							<div class="flex flex-col items-end gap-2">
								<button
									type="button"
									[disabled]="ts1RatingForm.invalid"
									(click)="onStepperNextReview(stepper)"
									data-variant="primary"
									class="step-submit aa-btn"
								>
									Next
								</button>
							</div>
						</div>
					</form>
				}
			</mat-step>

			<!-- STEP 3: Review & Final Rating -->
			<mat-step [stepControl]="ts1ReviewForm" [optional]="true">
				@if (ts1ReviewForm && stepper.selectedIndex === 2) {
					<form [formGroup]="ts1ReviewForm">
						<app-ts1-rating-step3-review
							[candidateNames]="candidateList"
							[candidateCrewRoles]="candidateCrewRoles"
							[candidateIds]="reviewCandidateIds"
							[currentIndex]="current"
							[competencyBlocks]="reviewCompetencyBlocks"
							[maneuverRows]="reviewManeuverRows"
							[maneuverOutcomes]="maneuverOutcomes"
							[temCompetencyOutcomes]="temCompetencyOutcomes"
							[summaryComments]="reviewSummaryComments"
							[traineeAssessmentId]="reviewTraineeAssessmentId"
							[signOffControl]="currentSignOffControl"
							[reminderNotesEnabled]="reminderNotesEnabledForForm"
							[useStructuredReminderNotes]="showEts1ReminderNotes"
							[reminderNoteResponses]="currentReminderNoteResponses"
							(prevCandidate)="onPrev()"
							(nextCandidate)="onNext()"
							(temCompetencyOutcomesChange)="onTemCompetencyOutcomesChange($event)"
							(maneuverOutcomesChange)="onManeuverOutcomesChange($event)"
							(outcomeComment)="onReviewOutcomeComment($event)"
							(summaryEdited)="onReviewSummaryEdited()"
							(recommendationChanged)="onReviewRecommendationChanged()"
							[scrollHighlightKey]="step3ScrollHighlightKeyForCurrentCandidate"
						></app-ts1-rating-step3-review>

						<div class="ts1-rating-form-v3__step-actions-wrap">
							@if (!isStep3CoreReviewComplete && step3ReviewIncompleteItems.length > 0) {
								<div class="ts1-signoff-gaps" role="region" aria-label="Items required before sign off">
									<div class="ts1-signoff-gaps__bar">
										<span class="ts1-signoff-gaps__count">
											{{ step3ReviewIncompleteItems.length }}
											{{ step3ReviewIncompleteItems.length === 1 ? "item" : "items" }} still required
										</span>
										<button
											type="button"
											class="ts1-signoff-gaps__toggle"
											(click)="toggleStep3GapsChecklist()"
											[attr.aria-expanded]="step3GapsChecklistExpanded"
										>
											{{ step3GapsChecklistExpanded ? "Hide list" : "See what's missing" }}
										</button>
									</div>
									@if (step3GapsChecklistExpanded) {
										<div class="ts1-signoff-gaps__list">
											@for (group of step3ReviewIncompleteGroups; track group.candidateIndex) {
												<div class="ts1-signoff-gaps__group">
													<div class="ts1-signoff-gaps__group-title">{{ group.candidateName }}</div>
													<ul class="ts1-signoff-gaps__items">
														@for (item of group.items; track item.reason) {
															<li>
																<button
																	type="button"
																	class="ts1-signoff-gaps__item"
																	(mousedown)="$event.preventDefault()"
																	(click)="goToStep3IncompleteItem(item, $event)"
																>
																	{{ item.label }}
																</button>
															</li>
														}
													</ul>
												</div>
											}
										</div>
									}
								</div>
							}

							<div
								class="ts1-rating-form-v3__step-actions ts1-rating-form-v3__step-actions--review flex flex-row justify-between gap-4"
							>
								<button type="button" class="aa-btn" data-variant="tertiary" (click)="onStepBack(stepper)">Back</button>
								<div class="flex-1"></div>
								<button
									type="button"
									(click)="onStepperNextSignOffClick(stepper)"
									data-variant="primary"
									class="step-submit aa-btn"
									[class.step-submit--inactive]="!isStep3CoreReviewComplete"
									[attr.aria-disabled]="!isStep3CoreReviewComplete || null"
								>
									Sign off
								</button>
							</div>
						</div>
					</form>
				}
			</mat-step>

			<!-- STEP 4: Sign-off -->
			<mat-step [stepControl]="ts1SignOffForm" [editable]="true">
				@if (ts1SignOffForm) {
					<form [formGroup]="ts1SignOffForm" (ngSubmit)="onRatingFormSubmit()">
						<ng-container formArrayName="signOffForm">
							@if (signOffForm.controls.length > 0) {
								<app-ts1-rating-step4-signoff
									[signOffForm]="signOffForm"
									(previewSummary)="togglePreviewSummary()"
								></app-ts1-rating-step4-signoff>
							}
						</ng-container>

						<div class="ts1-rating-form-v3__step-actions ts1-rating-form-v3__step-actions--signoff flex flex-row justify-between gap-4">
							<button type="button" class="aa-btn" data-variant="tertiary" (click)="onStepBack(stepper)">Back</button>

							<div class="flex flex-wrap justify-end gap-4">
								<button
									type="button"
									class="step-submit aa-btn"
									data-variant="primary"
									(click)="onSendToCandidate(stepper)"
									[disabled]="!canSendToCandidate || isSendingToCandidate"
								>
									{{ isSendingToCandidate ? "Sending…" : "Send to Candidate" }}
								</button>
							</div>
						</div>

						@if (signOffSuccess) {
							<div class="mt-4 bg-green-light p-3 text-green">Sign off completed successfully!</div>
						}
					</form>
				}
			</mat-step>
		</mat-stepper>
	</div>

	@if (inPreviewSummary) {
		<app-ts1-rating-preview-summary
			(close)="onPreviewFinished($event)"
			[candidateNames]="candidateList"
			[candidateCrewRoles]="candidateCrewRoles"
			[candidateIds]="reviewCandidateIds"
			[maneuverOutcomes]="maneuverOutcomes"
			[temCompetencyOutcomes]="temCompetencyOutcomes"
			[signOffForm]="signOffForm"
			[competencyBlocksForIndex]="previewCompetencyBlocksForIndex"
			[maneuverRowsForIndex]="previewManeuverRowsForIndex"
			[summaryCommentsForIndex]="previewSummaryCommentsForIndex"
			[competencyObCommentsForIndex]="previewCompetencyObCommentsForIndex"
			[traineeAssessmentIdForIndex]="previewTraineeAssessmentIdForIndex"
			[candidateDetailsForIndex]="previewCandidateDetailsForIndex"
			[reminderNotesEnabled]="reminderNotesEnabledForForm"
			[useStructuredReminderNotes]="showEts1ReminderNotes"
		></app-ts1-rating-preview-summary>
	}

	@if (isSendingToCandidate) {
		<div class="ts1-rating-form-v3__page-loading" role="status" aria-live="polite" aria-busy="true">
			<div class="ts1-rating-form-v3__page-loading-panel">
				<div class="ts1-rating-form-v3__page-loading-spinner" aria-hidden="true"></div>
				<p class="ts1-rating-form-v3__page-loading-title">Sending to candidate…</p>
				<p class="ts1-rating-form-v3__page-loading-subtitle">Please wait while the assessment is prepared.</p>
			</div>
		</div>
	}
</div>
